import { START_NODE_ID } from './nodeFactory';

const stripRow = row => {
  const { target_node_id: _t, ...rest } = row;
  return rest;
};

// simple BFS auto-layout for schemas saved without positions (JSON editor era)
const autoLayout = schema => {
  const positions = {};
  const nodesById = {};
  (schema.nodes || []).forEach(node => {
    nodesById[node.id] = node;
  });

  const outgoing = node => {
    const targets = [];
    if (node.next_node_id) targets.push(node.next_node_id);
    const config = node.config || {};
    (config.sections || []).forEach(section =>
      (section.rows || []).forEach(row => {
        if (row.target_node_id) targets.push(row.target_node_id);
      })
    );
    (config.buttons || []).forEach(button => {
      if (button.target_node_id) targets.push(button.target_node_id);
    });
    if (config.fallback_target_node_id) targets.push(config.fallback_target_node_id);
    return targets;
  };

  const levelCount = {};
  const queue = [];
  if (schema.start_node_id && nodesById[schema.start_node_id]) {
    queue.push([schema.start_node_id, 0]);
  }
  const seen = new Set();
  while (queue.length) {
    const [id, depth] = queue.shift();
    if (seen.has(id) || !nodesById[id]) continue;
    seen.add(id);
    levelCount[depth] = (levelCount[depth] || 0) + 1;
    positions[id] = { x: depth * 380, y: (levelCount[depth] - 1) * 200 };
    outgoing(nodesById[id]).forEach(target => queue.push([target, depth + 1]));
  }
  // orphans go to the bottom
  let orphanRow = 0;
  (schema.nodes || []).forEach(node => {
    if (!positions[node.id]) {
      positions[node.id] = { x: 0, y: 800 + orphanRow * 200 };
      orphanRow += 1;
    }
  });
  return positions;
};

// engine schema -> vue-flow nodes/edges (+ virtual start node)
export default function schemaToVueFlow(schema, t) {
  const safeSchema = schema && typeof schema === 'object' ? schema : {};
  const schemaNodes = Array.isArray(safeSchema.nodes) ? safeSchema.nodes : [];
  const fallbackPositions = schemaNodes.some(n => n.position) ? {} : autoLayout(safeSchema);

  const nodes = [];
  const edges = [];

  const pushEdge = (source, sourceHandle, target) => {
    if (!target) return;
    edges.push({
      id: `e-${source}-${sourceHandle}-${target}`,
      source,
      sourceHandle,
      target,
      targetHandle: 'in',
    });
  };

  schemaNodes.forEach(node => {
    const config = node.config || {};
    const cleanConfig = { ...config };
    delete cleanConfig.fallback_target_node_id;

    if (node.type === 'whatsapp_list') {
      cleanConfig.sections = (config.sections || []).map(section => ({
        ...section,
        rows: (section.rows || []).map(stripRow),
      }));
      (config.sections || []).forEach(section =>
        (section.rows || []).forEach(row =>
          pushEdge(node.id, `row-${row.id}`, row.target_node_id)
        )
      );
    }
    if (node.type === 'buttons') {
      cleanConfig.buttons = (config.buttons || []).map(stripRow);
      (config.buttons || []).forEach(button =>
        pushEdge(node.id, `btn-${button.id}`, button.target_node_id)
      );
    }
    if (['whatsapp_list', 'buttons', 'media'].includes(node.type)) {
      pushEdge(node.id, 'fallback', config.fallback_target_node_id);
    }
    if (['message', 'media'].includes(node.type)) {
      pushEdge(node.id, 'next', node.next_node_id);
    }

    nodes.push({
      id: node.id,
      type: node.type,
      position: node.position || fallbackPositions[node.id] || { x: 0, y: 0 },
      data: { title: node.title || node.type, config: cleanConfig },
    });
  });

  // virtual start node (not part of the engine schema)
  const startPosition =
    safeSchema.ui?.start_position ||
    (schemaNodes.length ? { x: (nodes[0]?.position?.x || 0) - 300, y: nodes[0]?.position?.y || 0 } : { x: 0, y: 0 });
  nodes.unshift({
    id: START_NODE_ID,
    type: 'start',
    position: startPosition,
    deletable: false,
    data: { title: t('FLOWS.BUILDER.NODES.START'), config: {} },
  });
  if (safeSchema.start_node_id) {
    pushEdge(START_NODE_ID, 'next', safeSchema.start_node_id);
  }

  return { nodes, edges, viewport: safeSchema.ui?.viewport || null };
}
